import * as Linking from "expo-linking"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import Icon from "react-native-vector-icons/AntDesign"
import {
  ApplePay,
  GooglePay,
  SquarePaymentsForm,
} from "react-square-web-payments-sdk"

import Base from "../../base"
import { AppButton, VenuePicker } from "../common"
import Gap from "../common/Gap"
import { subscriptionApi } from "../subscriptions/api"
import FormField from "../subscriptions/components/FormField"
import useModalPrompt from "../subscriptions/hooks/useModalPrompt"

export default function WebReceipt({ navigation }) {
  const [digits, setDigits] = useState("")
  const [errorDigits, setErrorDigits] = useState("")
  const [isLoading, setLoading] = useState(false)
  const [venue, setVenue] = useState(null)
  const [pickerVisible, setPickerVisible] = useState(false)

  const [ValidationErrorModal, { show: showValidationErrorModal }] =
    useModalPrompt(
      "Hold Up",
      "You've not entered a valid 4 digit number. Please check and try again.",
      {
        successText: "OK",
      },
    )

  const [NoTransactionsErrorModal, { show: showNoTransactionsErrorModal }] =
    useModalPrompt(
      "No Results",
      `Sorry there were no transactions found for ${errorDigits}.`,
      {
        successText: "OK",
      },
    )

  const getVenueFromName = useCallback(
    async venueName => {
      const response = await subscriptionApi.getSquareVenueFromName(venueName)
      if (response?.name) {
        setVenue(response)
      }
    },
    [setVenue],
  )

  useEffect(() => {
    Linking.getInitialURL().then(url => {
      var _url = url
      if (url.charAt(url.length - 1) === "/") {
        _url = _url.slice(0, -1)
      }
      let lastItem = _url.substring(_url.lastIndexOf("/") + 1)
      if (!lastItem || lastItem.length === 0) {
        lastItem = "monument"
      }
      getVenueFromName(lastItem)
    })
  }, [getVenueFromName])

  const onTextChange = useCallback(
    newText => {
      if (newText.length > 4) {
        setDigits(newText.substring(0, 4))
      } else {
        setDigits(newText)
      }
    },
    [setDigits],
  )

  const onApplePayAuth = useCallback(
    token => {
      if (token?.status === "Cancel") {
        return
      }
      const _digits = token?.details?.card?.last4
      setErrorDigits(`this ${walletTitle} Pay card`)
      if (!_digits) {
        showValidationErrorModal()
        return
      }
      showTransactions(_digits)
    },
    [showValidationErrorModal, showTransactions, setErrorDigits, walletTitle],
  )

  const onSubmit = useCallback(async () => {
    setErrorDigits(`card ending in ${digits}`)
    if (digits?.length < 4 || !Base.functions.isNumeric(digits)) {
      showValidationErrorModal()
      return
    }
    showTransactions(digits)
  }, [showValidationErrorModal, showTransactions, digits, setErrorDigits])

  const showTransactions = useCallback(
    async _digits => {
      setLoading(true)
      const transactions = await subscriptionApi.getReceiptTransactions(
        venue?.id,
        _digits,
      )
      setLoading(false)
      if (!transactions?.length) {
        showNoTransactionsErrorModal()
        return
      }
      navigation.navigate("Receipt Results", { transactions, venue })
    },
    [setLoading, showNoTransactionsErrorModal, venue, navigation],
  )

  const onVenueChange = useCallback(() => {
    setPickerVisible(true)
  }, [setPickerVisible])

  const onVenueSelected = useCallback(venue => {
    Linking.getInitialURL().then(url => {
      const baseURL = Base.functions.getBaseURL(url)
      window.location = baseURL + "/" + venue?.squareName?.toLowerCase()
    })
  }, [])

  const walletTitle = useMemo(() => {
    return window.ApplePaySession ? "Apple" : "Google"
  }, [])

  if (!venue) {
    return (
      <View style={[styles.main, { justifyContent: "center" }]}>
        <ActivityIndicator size={"large"} color={Base.colours.appWhite} />
      </View>
    )
  }

  return (
    <>
      <ScrollView style={styles.main} contentContainerStyle={styles.content}>
        <Image
          style={[styles.logo]}
          source={require("../../assets/images/red_logo_brushed.png")}
        />
        <Text style={styles.title}>NEED A RECEIPT?</Text>
        <Text style={styles.body}>
          Enter the last four digits of your debit or credit card to see
          transactions {venue?.name && "in "}
          {!!venue?.name && (
            <Text style={styles.bodyBold}>{venue.name}</Text>
          )}{" "}
          for the last 48 hours.
          {"\n\n"}You can then email yourself a VAT receipt.
        </Text>
        <View style={styles.row}>
          <FormField
            style={styles.formField}
            type={"numeric"}
            placeholder="Last 4 digits (e.g. 1234)"
            enabled={!isLoading}
            onChangeText={onTextChange}
            onSubmit={onSubmit}
            value={digits}
            returnKeyType={"next"}
          />
          <Gap horizontal size={15} />
          <AppButton style={styles.button} onPress={onSubmit}>
            <View style={styles.buttonIconContainer}>
              {isLoading ? (
                <ActivityIndicator
                  color={Base.colours.appRed}
                  animating
                  style={styles.spinner}
                />
              ) : (
                <Icon
                  name={"arrowright"}
                  size={30}
                  color={Base.colours.appRed}
                />
              )}
            </View>
          </AppButton>
        </View>
        <Gap size={18} />
        <Text style={styles.subtitle}>
          USED {walletTitle.toUpperCase()} PAY?
        </Text>
        <Text style={styles.body}>
          Tap below to authorize your {walletTitle} Pay card and see
          transactions. Don't worry we won't charge you a penny!
        </Text>
        <Gap size={18} />
        <SquarePaymentsForm
          applicationId={"sq0idp-jrgE4pIaum38d_OuPTnXGQ"}
          locationId={venue?.id ?? "2HY3ME7DVEE08"}
          cardTokenizeResponseReceived={(token, _) => {
            console.info("Token:", JSON.stringify(token))
            onApplePayAuth(token)
          }}
          createPaymentRequest={() => ({
            countryCode: "GB",
            currencyCode: "GBP",
            requestBillingContact: false,
            requestShippingContact: false,
            total: {
              amount: "0.0",
              label: "Nothing",
            },
          })}
        >
          <View style={styles.applePayContainer}>
            {window.ApplePaySession ? (
              <ApplePay />
            ) : (
              <GooglePay buttonType="short" />
            )}
          </View>
        </SquarePaymentsForm>
        {/* <View style={styles.applePayContainer}>
          <View style={styles.applePayPlaceholder} />
        </View> */}
        <TouchableOpacity
          style={styles.changeVenueButton}
          onPress={onVenueChange}
        >
          <Text style={styles.changeVenueTitle}>
            Not at {venue?.name?.replace("Vagabond ", "")}?{"\n"}Change venue
            <Icon name="right" color={Base.colours.appWhite} size={12} />
          </Text>
        </TouchableOpacity>
      </ScrollView>
      <ValidationErrorModal />
      <NoTransactionsErrorModal />
      <VenuePicker
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        onVenueSelected={onVenueSelected}
      />
    </>
  )
}

const styles = StyleSheet.create({
  main: {
    width: "100%",
    flex: 1,
    backgroundColor: Base.colours.appBrown,
  },

  logo: {
    marginTop: 20,
    width: "100%",
    height: 70,
    resizeMode: "contain",
  },

  content: {
    flexGrow: 1,
    width: "100%",
    padding: 33,
    alignItems: "center",
  },

  title: {
    textAlign: "center",
    fontFamily: Base.fonts.appTitle,
    color: Base.colours.appWhite,
    fontSize: 60,
    marginVertical: 18,
  },

  subtitle: {
    width: "100%",
    textAlign: "center",
    fontFamily: Base.fonts.appTitle,
    color: Base.colours.appWhite,
    fontSize: 40,
    marginVertical: 18,
  },

  body: {
    fontFamily: Base.fonts.appRegular,
    fontSize: 16,
    color: Base.colours.appWhite,
    lineHeight: 23,
  },

  bodyBold: {
    fontFamily: Base.fonts.appBold,
  },

  row: {
    marginTop: 34,
    flexDirection: "row",
    width: "100%",
    justifyContent: "center",
  },

  button: {
    width: 50,
    height: 50,
  },
  formField: {
    flex: 1,
  },
  buttonIconContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    alignContent: "center",
    marginBottom:
      Platform.OS === "web" ? 5 : Platform.OS === "android" ? 28 : 12,
  },

  spinner: {
    marginTop: Platform.OS === "web" ? 0 : 5,
  },

  changeVenueButton: {
    padding: 24,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    alignContent: "center",
    marginTop: 50,
  },

  changeVenueTitle: {
    fontFamily: Base.fonts.appBold,
    fontSize: 16,
    color: Base.colours.appWhite,
    marginHorizontal: 3,
    lineHeight: 23,
    textAlign: "center",
  },

  applePayContainer: {
    overflow: "hidden",
    marginTop: 6,
    borderRadius: 100,
    minWidth: 260,
    maxWidth: 560,
    height: 50,
    justifyContent: "center",
    width: "100%",
    backgroundColor: "black",
  },

  applePayPlaceholder: {
    width: "100%",
    height: 60,
    minWidth: 200,
    backgroundColor: "black",
    borderRadius: 5,
  },
})
